/**
 * Countdown Timer for Announcement Bar
 *
 * Two modes:
 *  - Fixed deadline: set data-deadline="YYYY-MM-DDTHH:MM:SS+10:00" on the element
 *    -> counts down to that exact instant and shows days (used for the EOFY sale end).
 *  - Daily (default, no attribute): counts down to 12:00 PM Brisbane time, rolling
 *    to the next day once noon passes (the original "order before noon" behaviour).
 */
class AnnouncementCountdown extends HTMLElement {
  constructor() {
    super();
    this.targetHour = 12; // 12:00 PM Brisbane time (daily mode)
    this.targetMinute = 0;
    this.intervalId = null;
    this.deadline = null; // Date | null (fixed-deadline mode)
  }

  connectedCallback() {
    const raw = this.getAttribute('data-deadline');
    if (raw) {
      const parsed = new Date(raw);
      if (!isNaN(parsed.getTime())) {
        this.deadline = parsed;
      }
    }
    this.render();
    this.startCountdown();
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  render() {
    this.innerHTML = `
      <span class="countdown-timer" role="timer" aria-live="off">
        <span class="countdown-label">ENDS IN</span>
        <span class="countdown-time"></span>
      </span>
    `;
  }

  getBrisbaneTime() {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  }

  getDailyTarget() {
    const brisbaneNow = this.getBrisbaneTime();
    const target = new Date(brisbaneNow);
    target.setHours(this.targetHour, this.targetMinute, 0, 0);
    if (brisbaneNow >= target) {
      target.setDate(target.getDate() + 1);
    }
    return { now: brisbaneNow, target };
  }

  calculateTimeRemaining() {
    let diff;
    if (this.deadline) {
      // Absolute instant (the ISO string carries its own offset).
      diff = this.deadline.getTime() - Date.now();
    } else {
      const { now, target } = this.getDailyTarget();
      diff = target.getTime() - now.getTime();
    }

    if (diff <= 0) {
      return null;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }

  updateDisplay() {
    const timeRemaining = this.calculateTimeRemaining();
    const timeElement = this.querySelector('.countdown-time');
    const timerElement = this.querySelector('.countdown-timer');

    if (!timeRemaining) {
      if (this.deadline) {
        // Keep the bar stable until the campaign shutdown removes it.
        if (this.intervalId) clearInterval(this.intervalId);
        if (timeElement) timeElement.textContent = 'ENDING NOW';
        if (timerElement) timerElement.setAttribute('aria-label', 'Offer ending now');
        return;
      }
      // Daily mode: roll to the next day's target.
      this.startCountdown();
      return;
    }

    if (!timeElement) return;

    if (this.deadline) {
      const { days, hours, minutes } = timeRemaining;
      const paddedHours = String(hours).padStart(2, '0');
      const paddedMinutes = String(minutes).padStart(2, '0');
      timeElement.textContent = `${days}D ${paddedHours}H ${paddedMinutes}M LEFT`;
      timerElement.setAttribute('aria-label', `${days} days, ${hours} hours and ${minutes} minutes remaining`);
    } else {
      const parts = [];
      if (timeRemaining.hours > 0) parts.push(`${timeRemaining.hours} hrs`);
      parts.push(`${timeRemaining.minutes} mins`);
      parts.push(`${timeRemaining.seconds} secs`);
      timeElement.textContent = parts.join(' : ');
    }
  }

  startCountdown() {
    this.updateDisplay();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    const refreshRate = this.deadline ? 15000 : 1000;
    this.intervalId = setInterval(() => {
      this.updateDisplay();
    }, refreshRate);
  }
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
